import React, { Component } from 'react';

import { objToFen } from 'chessboard-element';
import { withRouter } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Switch from '@material-ui/core/Switch';

import { parseFenFromUrl, reverseFenOrientation } from '../../utils';

import { withStyles } from '@material-ui/styles';
import settings from '../../settings';

const playerSwitchStyles = (theme) => ({
  switchBase: {
    color: 'black',
    '&$checked': {
      color: 'white',
    },
    '&$checked + $track': {
      backgroundColor: 'gray',
    },
  },
  checked: {},
  track: {
    backgroundColor: 'gray'
  },
});

const PlayerSwitch = withStyles(playerSwitchStyles)(Switch);

const styles = theme => ({
  container: {
    marginTop: 60,  // TODO: remember that this is just for injecting the page as iframe into browser extension
    maxWidth: 600, // TODO: set this to 1600 maybe?
    margin: '0 auto',
    justifyContent: 'space-evenly',
    backgroundColor: 'rgb(237, 235, 233)',
  },
  topBar: {
  },
  actionsContainer: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  switchWrapper: {
    marginLeft: 8,
    marginRight: 12,
    display: 'flex',
    flexDirection: 'column',
    marginTop: -4,
    width: 80,
  },
  buttonWrapper: {
    marginLeft: 0,
    marginRight: 5,
  },
  button: {
    textTransform: 'none',
  },
  optionsContainer: {
  },
});

const addOptionsToFen = (fen, currentPlayer, castling) => {
  let castingStr = '';
  if (castling.white.kingside) {
    castingStr += 'K';
  }
  if (castling.white.queenside) {
    castingStr += 'Q';
  }
  if (castling.black.kingside) {
    castingStr += 'k';
  }
  if (castling.black.queenside) {
    castingStr += 'q';
  }
  if (castingStr === '') {
    castingStr = '-';
  }
  return fen + ' ' + (currentPlayer === 'white' ? 'w' : 'b') + ' ' + castingStr + ' - 0 1';
};

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

class BoardEditor extends Component {
  constructor(props) {
    super(props);

    const searchParams = new URLSearchParams(props.location.search);

    const paramFen = searchParams.get('fen');
    this.initFen = parseFenFromUrl(paramFen ?? props.match?.params.fen ?? START_FEN);
    const parts = this.initFen.split(' ');
    const currentPlayer = parts.length >= 2 && parts[1] === 'b' ? 'black' : 'white';
    const castlingStr = parts.length >= 3 ? parts[2] : 'KQkq';

    const paramColor = searchParams.get('color');
    this.color = ['white', 'black'].includes(paramColor) ? paramColor : 'white';

    this.state = {
      currentPlayer,
      castling: {
        white: {
          kingside: castlingStr.includes('K'),
          queenside: castlingStr.includes('Q'),
        },
        black: {
          kingside: castlingStr.includes('k'),
          queenside: castlingStr.includes('q'),
        },
      },
      showCastling: false,
    };
    this.board = null;
  }

  componentDidMount() {
    this.board.addEventListener('change', this.onBoardUpdate);
  }

  componentWillUnmount() {
    this.board?.removeEventListener('change', this.onBoardUpdate);
  }

  // The event includes the new position before chessboard-element updates
  // its property. Publish that exact value, with no timer or stale URL window.
  onBoardUpdate = event => this.updateUrl(objToFen(event.detail.value));

  updateUrl = (placement = this.board.fen()) => {
    const { currentPlayer, castling } = this.state;
    const fenWithOptions = addOptionsToFen(placement, currentPlayer, castling);
    const params = new URLSearchParams(window.location.search);
    params.delete('fen');
    params.set('color', this.color);
    window.history.replaceState(window.history.state, '', `${settings.routes.BOARD_EDITOR}/${fenWithOptions.replace(/ /g, '_')}?${params}`);
  }

  handlePlayerChange = (e) => {
    this.setState({
      currentPlayer: e.target.value === 'white' ? 'black' : 'white',
    }, () => {
      this.updateUrl();
    });
  }

  handleOrientationChange = (e) => {
    const reversed = reverseFenOrientation(this.board.fen());
    this.board.setPosition(reversed);
    this.updateUrl();
  }

  // TODO: do we need this? Considering that our mobile app already has Apply button that takes URL of /editor and uses it 
  //  to display the analysis board having all the logic there
  handleApply = () => {
    const { history } = this.props;
    const { currentPlayer, castling } = this.state;
    const fen = addOptionsToFen(this.board.fen(), currentPlayer, castling);
    if (fen) history.push(`${settings.routes.BOARD_ANALYSIS}/${fen.replace(/ /g, '_')}?color=${this.color}`);
  }

  handleCastlingChange = (e) => {
    const [ color, type ] = e.target.getAttribute('name').split('_');
    const checked = e.target.checked;
    this.setState(state => ({
      castling: { ...state.castling, [color]: { ...state.castling[color], [type]: checked } },
    }), () => this.updateUrl());
  }

  toggleCastling = () => {
    this.setState({
      showCastling: !this.state.showCastling,
    });
  }

  clearBoard = () => {
    this.board.setPosition({});
    this.updateUrl();
  }

  render() {
    const { classes } = this.props;
    const { showCastling } = this.state;
    const { currentPlayer, castling } = this.state;

    return (
      <div className={classes.container}>
        <div className={classes.topBar}>
          <div className={classes.actionsContainer}>
            {/*}
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => this.handleApply()}
            >Analyze</Button>
            */}
            <FormControlLabel
              className={classes.switchWrapper}
              control={
                <PlayerSwitch
                  className={classes.playerSwitch}
                  checked={currentPlayer === 'white'}
                  size="small"
                  inputProps={{ 'aria-label': 'White to move' }}
                  onChange={this.handlePlayerChange}
                  value={currentPlayer}
                />
               }
               label={<div style={{ marginTop: -6, fontSize: '0.75rem' }}>{currentPlayer === 'white' ? 'White' : 'Black'} to play</div>}
               labelPlacement="start"
             />
            {/*}
            <FormControlLabel
              className={classes.buttonWrapper}
              control={
                <Button
                className={classes.button}
                size="small"
                variant="outlined"
                onClick={this.handleOrientationChange}
              >A1⇄H8</Button>
              }
             />
            */}
            <FormControlLabel
              className={classes.buttonWrapper}
              control={
                <Button
                className={classes.button}
                size="small"
                variant="outlined"
                onClick={this.toggleCastling}
              >Castling</Button>
              }
             />
            <FormControlLabel
              className={classes.buttonWrapper}
              control={
                <Button
                className={classes.button}
                size="small"
                variant="outlined"
                onClick={this.clearBoard}
              >Clear Board</Button>
              }
             />
          </div>
          <div className={classes.optionsContainer}>
             { showCastling && (
               <FormControl component="fieldset" className={classes.castlingFormControl}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={castling.white.kingside}
                        onChange={this.handleCastlingChange}
                        name="white_kingside"
                    />}
                    label={<span><strong>White</strong>: O-O</span>}
                    labelPlacement="start"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={castling.white.queenside}
                        onChange={this.handleCastlingChange}
                        name="white_queenside"
                    />}
                    label="O-O-O"
                    labelPlacement="start"
                  />
                </FormGroup>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={castling.black.kingside}
                        onChange={this.handleCastlingChange}
                        name="black_kingside"
                    />}
                    label={<span><strong>Black</strong>: O-O</span>}
                    labelPlacement="start"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={castling.black.queenside}
                        onChange={this.handleCastlingChange}
                        name="black_queenside"
                    />}
                    label="O-O-O"
                    labelPlacement="start"
                  />
                </FormGroup>
              </FormControl>
             )}
         </div>
        </div>
        { /* in chess-board we modify state directly without setState to avoid infinite loop of update-render */}
        <chess-board
          position={this.initFen.split(' ')[0]}
          style={{ width: '100%' }}
          orientation={this.color}
          aria-label="Edit chess position"
          draggable-pieces
          spare-pieces
          drop-off-board="trash"
          ref={(e) => this.board = e}>
        </chess-board>
      </div>
    );
  }
}

export default withStyles(styles)(withRouter(BoardEditor));
